import { useCallback, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { Analytics, roomStatistic } from "../../analytics";
import { ReportReason, StringOrNumber } from "../../types";
import {
  getBlockedFriends,
  getUnBlockedFriends,
  removeFriend,
  reportUser,
  saveFriend,
  updateFriend,
} from "../api/helpers/apiFunctions/friends";
import { usePastimeToast } from "../components/Toast";
import { useUser } from "../hooks/useUser";
import { messages } from "../locale";

enum FriendsCacheKeys {
  FRIENDS = "friends",
  BLOCKED = "blocked",
}

export const useFriendsQuery = () =>
  useQuery(FriendsCacheKeys.FRIENDS, getUnBlockedFriends, {
    onSuccess: (data) => {
      Analytics.setSuperProperties({
        "Total Friends": data.length,
      });
    },
  });

export const useBlockedFriendsQuery = () => useQuery(FriendsCacheKeys.BLOCKED, getBlockedFriends);

export const useFriendsMutations = () => {
  const {
    userDetails: { userId = 0 },
  } = useUser();
  const toast = usePastimeToast();
  const queryClient = useQueryClient();

  const { mutateAsync: save } = useMutation(
    ({ userId, friendId }: { userId: StringOrNumber; friendId: StringOrNumber }) => saveFriend(userId, friendId, false),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(FriendsCacheKeys.FRIENDS);
      },
    }
  );

  const { mutateAsync: remove } = useMutation(
    ({ userId, friendId }: { userId: StringOrNumber; friendId: StringOrNumber }) => removeFriend(userId, friendId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(FriendsCacheKeys.FRIENDS);
        toast({
          title: messages.videoCall.friendRemoved,
        });
      },
    }
  );

  const { mutateAsync: report } = useMutation(
    ({ userId, friendId, reason }: { userId: number; friendId: number; reason: ReportReason }) =>
      reportUser(friendId, userId, reason),
    {
      onSuccess: (_, { friendId }) => {
        queryClient.invalidateQueries(FriendsCacheKeys.FRIENDS);
        queryClient.invalidateQueries(FriendsCacheKeys.BLOCKED);
        roomStatistic.blocked.add(Number(friendId));
        toast({
          title: messages.videoCall.yourReportSent,
        });
      },
    }
  );

  const { mutateAsync: update } = useMutation(
    ({ userId, friendId, blocked }: { userId: StringOrNumber; friendId: StringOrNumber; blocked: boolean }) =>
      updateFriend(userId, friendId, blocked),
    {
      onSuccess: (_, { blocked, friendId }) => {
        queryClient.invalidateQueries(FriendsCacheKeys.FRIENDS);
        queryClient.invalidateQueries(FriendsCacheKeys.BLOCKED);

        if (blocked) {
          roomStatistic.blocked.add(Number(friendId));
        } else {
          roomStatistic.blocked.delete(Number(friendId));
          toast({
            title: messages.videoCall.friendIsUnblocked,
          });
        }
      },
    }
  );

  const handleSaveFriend = useCallback(
    async (friendId: StringOrNumber) => {
      if (userId) {
        await save({ userId, friendId });
      }
    },
    [userId, save]
  );

  const handleRemoveFriend = useCallback(
    async (friendId: StringOrNumber) => {
      if (userId) {
        await remove({ userId, friendId });
      }
    },
    [userId, remove]
  );

  const handleReportFriend = useCallback(
    async (friendId: number, reason: ReportReason) => {
      if (userId) {
        await report({ userId, friendId, reason });
      }
    },
    [userId, report]
  );

  const handleUpdateUser = useCallback(
    async (friendId: StringOrNumber, blocked: boolean) => {
      if (userId) {
        await update({ userId, friendId, blocked });
      }
    },
    [userId, update]
  );

  return useMemo(
    () => ({ handleSaveFriend, handleRemoveFriend, handleReportFriend, handleUpdateUser }),
    [handleSaveFriend, handleRemoveFriend, handleReportFriend, handleUpdateUser]
  );
};
